import React, { useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { Form, Container, Button, Toast, Spinner } from "react-bootstrap";
import styles from "./job.module.css";
import { jobAPi } from "../../../CONSTANTS";
import { axiosFileUploadPost } from "../../../UTILS/AXIOS";
import FormSecurity from "../../FORM_SECURITY";
import {
  rules,
  securityFields,
  isSuccess,
  failureMessage,
  applyServerErrors,
} from "../../../UTILS/VALIDATION";

const MAX_RESUME_BYTES = 8 * 1024 * 1024;

const schema = Yup.object().shape({
  name: rules.name("Name"),
  phone: rules.phone(),
  email: rules.email(),
  resume: Yup.mixed()
    .required("Please attach your resume")
    .test("fileType", "Resume must be a PDF file", (file) => !file || file.type === "application/pdf")
    .test("fileSize", "Resume must be 8 MB or smaller", (file) => !file || file.size <= MAX_RESUME_BYTES),
  ...securityFields,
});

const formFields = [
  { name: "name", label: "Name", type: "text", autoComplete: "name" },
  { name: "phone", label: "Phone", type: "tel", autoComplete: "tel" },
  { name: "email", label: "Your email", type: "email", autoComplete: "email" },
];

const JobApplicationForm = () => {
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastVariant, setToastVariant] = useState("success");
  const [loading, setLoading] = useState(false);
  const captcha = useRef(null);
  const fileInput = useRef(null);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    setError,
    formState: { errors },
  } = useForm({ resolver: yupResolver(schema), mode: "onTouched" });

  const onFileChange = (event) => {
    setValue("resume", event.target.files[0] || undefined, { shouldValidate: true });
  };

  const onSubmit = async (data) => {
    setLoading(true);
    const result = await axiosFileUploadPost(jobAPi, { ...data, formType: "job" });
    if (isSuccess(result)) {
      setToastMessage("Thank you for applying. We'll review your resume and be in touch.");
      setToastVariant("success");
      reset();
      if (fileInput.current) fileInput.current.value = "";
    } else {
      applyServerErrors(result, setError, Object.keys(schema.fields));
      setToastMessage(failureMessage(result));
      setToastVariant("danger");
    }
    captcha.current?.reset();
    setShowToast(true);
    setLoading(false);
  };

  return (
    <Container className={styles.jobForm}>
      <Form noValidate onSubmit={handleSubmit(onSubmit)} className={styles.jobFormFormSection}>
        {formFields.map(({ name, label, type, autoComplete }) => (
          <Form.Group controlId={`form${name}`} key={name}>
            <Form.Label>
              {label}
              <span className={styles.jobFormTextDanger}>*</span>
            </Form.Label>
            <Form.Control
              type={type}
              {...register(name)}
              autoComplete={autoComplete}
              aria-required="true"
              isInvalid={!!errors[name]}
              disabled={loading}
              className={styles.jobFormFormControl}
            />
            <Form.Control.Feedback type="invalid">{errors[name]?.message}</Form.Control.Feedback>
          </Form.Group>
        ))}

        <Form.Group controlId="formresume">
          <Form.Label>
            Attach Resume (PDF, up to 8 MB)
            <span className={styles.jobFormTextDanger}>*</span>
          </Form.Label>
          <Form.Control
            ref={fileInput}
            type="file"
            accept="application/pdf"
            onChange={onFileChange}
            aria-required="true"
            isInvalid={!!errors.resume}
            disabled={loading}
            className={styles.jobFormFormControl}
          />
          <Form.Control.Feedback type="invalid">{errors.resume?.message}</Form.Control.Feedback>
        </Form.Group>

        <FormSecurity ref={captcha} register={register} setValue={setValue} error={errors.recaptchaToken} theme="dark" />

        <Form.Group>
          <div className="d-flex justify-content-center mt-4">
            <Button type="submit" disabled={loading} className={styles.jobFormFormSubmit}>
              {loading ? <Spinner animation="border" size="sm" /> : "Submit"}
            </Button>
          </div>
        </Form.Group>
      </Form>

      <Toast
        onClose={() => setShowToast(false)}
        show={showToast}
        delay={6000}
        autohide
        role="status"
        className={`bg-${toastVariant} text-white position-fixed`}
        style={{ bottom: "10px", right: "10px", zIndex: 10000 }}
      >
        <Toast.Body>{toastMessage}</Toast.Body>
      </Toast>
    </Container>
  );
};

export default JobApplicationForm;
